// vendor
import { defineComponent, ref, computed, watch, provide } from "@vue/composition-api";
// project
import * as SessionAPI from "@/services/api/sessions";
import * as StudyAPI from "@/services/api/studies";
import * as TimeUtils from "@/utils/time";
import { RunGraph } from "@/components.run/run-graph";
import { Session } from "@/psychlab/types/sessions";
import { StudyInfo } from "@/psychlab/types/studies";
import { Graph } from "@/psychlab/types/graph";
import { isMobile as isMobileDevice } from "@/utils/device";
import { Dictionary } from "@/utils/types";
// local
import { default as StartSplash } from "../vue/start-splash.vue";
import { default as PageNotFound } from "../vue/page-not-found.vue";
import { default as EndScreen } from "../vue/end-screen.vue";
import { default as SessionProgressBar } from "../vue/session-progress-bar.vue";
import { default as DeviceSetup } from "../vue/device-setup.vue";
import { SessionManager } from "./SessionManager";
import { SessionContext } from "./SessionContext";
import { getProgressPath } from "./getProgressPath";
import { SetupContext, SetupResult } from "./DeviceSetup";

const checkAccelerometerRequired = (g:Graph, p:string[]) => {
	const types = [ "Scene", "Gallery" ];
	return p.map(nid => g.nodes[nid]).findIndex(n => types.includes(n.type)) > -1;
};

export const RunSession = defineComponent({

	components:{
		RunGraph,
		StartSplash,
		PageNotFound,
		EndScreen,
		SessionProgressBar,
		DeviceSetup,
	},
	setup(props, context){

		const error = ref(false);
		const showSplash = ref(true);
		const loading = ref(false);
		const session = ref<Session>();
		const study = ref<StudyInfo>();
		const currentProgressStep = ref("");
		const progressReady = ref(true);

		const setupReady = ref(false);

		const useAccelerometer = ref(false);

		const progressVisible = computed(() => {
			if(!session.value){ return false; }
			if(finished.value){ return false; }
			if(study.value && !study.value.showProgress){
				return false;
			}
			return progressReady.value;
		});

		const sessionId = computed(() => context.root.$route.params["sessionId"]);

		const sessionName = computed(() => session.value ? session.value.name : undefined);

		const finished = computed(() => {
			return !session.value ? false : session.value.state.node === null;
		});

		const manager = computed(() => {
			return sessionId.value ? new SessionManager(sessionId.value) : null;
		});

		const isMobile = computed(() => {
			return isMobileDevice();
		});

		const accelerometerEnabled = computed(() => {
			// return true;
			return useAccelerometer.value;
		});

		const requiresAccelerometer = computed(() => {
			if(!isMobile.value){ return false; }
			const g = session.value ? session.value.graph : null;
			const p = session.value ? session.value.path : null;
			if(!g || !p){ return null; }
			return checkAccelerometerRequired(g, p);
		});

		const progressSteps = computed(() => {
			if(!session.value || !session.value.graph){ return []; }
			return getProgressPath({
				graph:session.value.graph,
				path:session.value.path
			});
		});

		const cache = computed<Dictionary<any>>(() => {
			return context.root.$store.state.cache["storage"];
		});
		
		const deviceSetupContext = computed<SetupContext>(() => {
			return {
				checkAccelerometer:requiresAccelerometer.value || false,
			};
		})

		watch(sessionName, (v) => {
			if(v !== undefined){ document.title = v; }
		});

		watch(session, () => {
			if(!session.value || !session.value.graph){
				return;
			}
		});
		
		const completeDeviceSetup = (r:SetupResult) => {
			useAccelerometer.value = r.accelerometerEnabled;
			setupReady.value = true;
		};

		const loadSessionCache = () => {
			const sc = SessionAPI.getSessionCache(sessionId.value);
			if(sc){
				Object.keys(sc).forEach(k => context.root.$store.commit("cache/setValue", { k, v:sc[k] }));
			}
		};

		const setProgressStep = (name:string) => {
			currentProgressStep.value = name;
		};

		const progressStepCount = computed(() => {
			return progressSteps.value.length;
		});

		const progressStepIndex = computed(() => {
			return progressSteps.value.findIndex(s => s === currentProgressStep.value);
		});

		const init = async() => {
			loading.value = true;
			try{
				session.value = await SessionAPI.loadSession(sessionId.value);
				if(session.value){
					study.value = await StudyAPI.getStudyInfo(session.value.group);
				}
			}
			catch(err){
				error.value = true;
				console.error(err);
			}

			if(!finished.value){
				loading.value = false;
				await TimeUtils.getDelay(1000);
				showSplash.value = false;
			}
			else {
				showSplash.value = false;
				loading.value = false;
			}
			
			if(!isMobile.value){
				setupReady.value = true;
			}

			loadSessionCache();
		};

		init();

		provide("setProgressReady", (v:boolean) => {
			progressReady.value = v;
		});

		provide("setProgressStep", setProgressStep);

		provide("showProgressBar", progressVisible);

		provide("getSessionContext", ():SessionContext => {
			return {
				sessionId,
				isMobile,
				progressStepCount,
				progressStepIndex,
				progressVisible,
				accelerometerEnabled,
				cache,
				setProgressStep,
			};
		});

		return {
			showProgress:progressVisible,
			error,
			showSplash,
			loading,
			session,
			sessionId,
			finished,
			manager,
			currentProgressStep,
			setupReady,
			deviceSetupContext,
			completeDeviceSetup,
		};
	}
});